import React, { useState, useEffect, useRef, cloneElement, Children, Fragment } from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import lodash from 'lodash';
import classnames from 'classnames';
import styles from './StudyExpectations.scss';
import SideNav from 'pages/studyOverview/components/SideNav';
import { Tabs } from 'components/common/Tabs/Tabs';
import { usePrevious } from 'common/util/hookHelpers';
import AddExpectationsPanel from 'pages/studyOverview/containers/StudyExpectations/AddExpectationsPanel';
import { Button, BUTTON } from 'components/common/Button/Button';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import FilePicker from 'components/common/File/FilePicker';
import { loadMimeTypes } from 'common/actions/fileUploadActions';
import { ExpectationsModal } from './ExpectationsModal/ExpectationsModal';
import { deleteUnfiledDocument } from 'part11/actions/documents';
import { removeFiles } from 'binder/actions/files';
import { PageTitle, PageDescription } from 'components/common/Study/PageComponents';
import { isStudyStartUp, STUDY_START_UP, OTHER_STAGES } from 'common/util/studyHelpers.js';
import CancelModal from 'pages/study/containers/CancelModal';
import { UserCanManageStudyExpectations } from 'common/permissions/components/actionButton';
import { Conditional } from 'common/components/layout';
import { browserHistory } from 'react-router';
import { getExpectationsUploadedFilesCount } from 'pages/studyOverview/selectors/expectationsDocuments';

import {
    TwoColumnScaffold,
    LeftColumn,
    RightColumn
} from 'pages/studyOverview/components/TwoColumnScaffold';

import {
    evaluateStudyExpectations,
    findAndAssignDuplicateDocuments,
    unloadStudyExpectations,
    unloadUploadedDocuments,
} from '../../actions/studyExpectations';
import { loadHomePageV4Access } from '../../../../common/features/predicates';

const CANCEL_MODAL_TITLE = 'Are you sure you want to leave this page?';
const CANCEL_MODAL_ONCANCEL_BUTTON_TEXT = 'No, do not leave page';
const CANCEL_MODAL_ONSAVE_BUTTON_TEXT = 'Yes, leave page';

const buildStudyExpectationLinks = (studyId, missingExpectationsCount) => ([
    {
        to: `/study/${studyId}/expectations/all-documents`,
        title: 'All Documents',
    },
    {
        pill: missingExpectationsCount,
        to: `/study/${studyId}/expectations/missing-documents`,
        title: 'Missing Documents',
    },
]);

export function StudyExpectations(props) {
    const sideNavLinks = buildStudyExpectationLinks(props.study.id, props.missingExpectationsCount);
    const [studyExpectationWithFile, setUploadedFileToExpectation] = useState('');
    const [teamMemberIdUpload, setTeamMemberIdUpload] = useState('');
    const [toRefresh, setToRefresh] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [uploadedDocumentDetails, setUploadedDocumentDetails] = useState();
    const filePickerRef = useRef(null);
    const files = props.files;
    const prevFiles = usePrevious({ files });
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [nextLocation, setNextLocation] = useState('');
    const unregisterLeaveHook = useRef();

    const routerWillLeave = (location) => {
        if (lodash.isEmpty(files)) {
            return true;
        }
        
        let newLocation = location.pathname;

        if (location.search) {
            newLocation += location.search;
        }

        if (props.expectationsUploadedFilesCount === 0) {
            goToLocation(newLocation);
            return false;
        }

        const isFilingUploadedFile = newLocation.includes(`files/${files.lastUploadedDocument.id}`);
        if (isFilingUploadedFile && props.expectationsUploadedFilesCount === 1) {
            goToLocation(newLocation);
            return false;
        }

        setNextLocation(newLocation);
        openCancelModal();
        return false;
    };

    /**
     * Go to location without the nextLocation state check.
     * Useful for when we don't need the modal check.
     * @param {string} location
     */
    const goToLocation = (location) => {
        const { router } = props;
        unregisterLeaveHook.current();
        return router.push(location);
    };

    const goToNextLocation = () => {
        const { router } = props;
        unregisterLeaveHook.current();
        if (nextLocation) {
            return router.push(nextLocation);
        }
    };

    const leavePage = () => {
        goToNextLocation();
    };

    const closeCancelModal = () => {
        setCancelModalOpen(false);
        setNextLocation('');
    };

    const openCancelModal = () => {
        setCancelModalOpen(true);
    };

    const reevaluateStudyExpectations = function () {
        if (toRefresh) {
            setToRefresh(toRefresh => !toRefresh);
            props.evaluateStudyExpectations();
            props.removeFiles();
        }
    };

    useEffect(() => {
        const { router, route } = props;
        unregisterLeaveHook.current = router.setRouteLeaveHook(route, routerWillLeave);

        return () => {
            unregisterLeaveHook.current();
        };
    }, [props.expectationsUploadedFilesCount]);

    useEffect(() => {
        const prevLastUploadedDocument = prevFiles && prevFiles.lastUploadedDocument;
        const lastUploadedDocument = files && files.lastUploadedDocument;
        const lastUploadedDocumentChanged = prevLastUploadedDocument !== lastUploadedDocument;

        if (lastUploadedDocumentChanged && files.hasNewDocumentConversionResult) {
            filePickerRef.current.open();
            props.findAndAssignDuplicateDocuments(studyExpectationWithFile, { ...files.lastUploadedDocument, teamMemberId: teamMemberIdUpload });
        }
    }, [files]);

    useEffect(() => {
        props.loadMimeTypes();
        props.evaluateStudyExpectations();

        return () => {
            props.unloadStudyExpectations();
            props.removeFiles();
        };
    }, []);

    useEffect(() => {
        window.addEventListener('focus', reevaluateStudyExpectations, true);
        return () => {
            window.removeEventListener('focus', reevaluateStudyExpectations, true);
        };
    }, [toRefresh]);

    const openFilePicker = () => {
        filePickerRef.current.openFilePicker();
    };

    function onDelete(documentId) {
        setOpenModal(false);
        props.unloadUploadedDocuments(studyExpectationWithFile);
        props.deleteUnfiledDocument(documentId, true);
    }

    function onFile(documentId, fileType, documentTypeId, closeModal) {
        if (closeModal) {
            setOpenModal(false);
        }

        const path = `/study/${props.study.id}/files/${documentId}/${fileType}?rd=none&from=expectations`;
        const newTabPath = documentTypeId ? `${path}&dt=${documentTypeId}` : path;
        browserHistory.push(newTabPath);
    }

    function onError(uploadedDocument, fileType, documentTypeId) {
        setOpenModal(true);
        setUploadedDocumentDetails({ uploadedDocument, fileType, documentTypeId, });
    }

    return (
        <Fragment data-cy="StudyExpectations">
            <TwoColumnScaffold>
                <LeftColumn>
                    <SideNav
                        activeIndex={() =>
                            lodash.findIndex(sideNavLinks, link => props.location.pathname.includes(link.to))
                        }
                        links={sideNavLinks}
                    />
                </LeftColumn>
                <RightColumn>
                    <StudyExpectationsContent
                        setUploadedFileToExpectation={setUploadedFileToExpectation}
                        openFilePicker={openFilePicker}
                        setTeamMemberIdUpload={setTeamMemberIdUpload}
                        onError={onError}
                        onFile={onFile}
                        {...props}
                    />
                    <FilePicker
                        ref={filePickerRef}
                        location={location}
                        multiple={false}
                        binderId={props.study.binderId}
                        institutionId={props.study.institutionId}
                        allowedMimeTypes={props.allowedMimeTypes}
                        fileExtensionLookup={props.fileExtensionLookup}
                    />
                    {openModal && <ExpectationsModal
                        binderId={props.binderId}
                        documentTypes={props.documentTypes}
                        modalDetails={uploadedDocumentDetails}
                        onClose={() => setOpenModal(false)}
                        onDelete={onDelete}
                        onFile={onFile}
                        open={openModal}
                    />}
                </RightColumn>
            </TwoColumnScaffold>
            <CancelModal
                isOpen={cancelModalOpen}
                onClose={closeCancelModal}
                onCancel={closeCancelModal}
                onSave={leavePage}
                title={CANCEL_MODAL_TITLE}
                helperText={<ModalHelperText />}
                onCancelText={CANCEL_MODAL_ONCANCEL_BUTTON_TEXT}
                onSaveText={CANCEL_MODAL_ONSAVE_BUTTON_TEXT}
            />
        </Fragment>
    );
}

export default connect(state => {
    const documentTypes = lodash.get(state, 'me.documentTypes', []);
    const allExpectedStudyDocuments = lodash.get(state, 'studyExpectations.allExpectedStudyDocuments', []);
    const allExpectedStaffDocuments = lodash.get(state, 'studyExpectations.allExpectedStaffDocuments', []);
    const missingExpectedStudyDocuments = lodash.get(state, 'studyExpectations.missingExpectedStudyDocuments', []);
    const missingExpectedStaffDocuments = lodash.get(state, 'studyExpectations.missingExpectedStaffDocuments', []);
    const missingExpectationsCount = lodash.get(state, 'studyExpectations.missingExpectationsCount', 0);
    const allStudyTeam = lodash.get(state, 'studySettings.studyTeam', []);
    const studyTeam = lodash.filter(allStudyTeam, teamMember => !lodash.isEmpty(teamMember.studyRole));
    const isLoading = lodash.get(state, 'studyExpectations.isLoading', true);
    const allowedMimeTypes = lodash.get(state, 'app.allowedMimeTypes', []);
    const fileExtensionLookup = lodash.get(state, 'app.fileExtensionLookup', {});
    const lastUploadedDocument = lodash.get(state, 'files.lastUploadedDocument', {});
    const binderId = lodash.get(state, 'study.binderId');
    const files = lodash.get(state, 'files', {});
    const expectationsUploadedFilesCount = getExpectationsUploadedFilesCount(state);

    return {
        allowedMimeTypes,
        binderId,
        documentTypes,
        allExpectedStudyDocuments,
        allExpectedStaffDocuments,
        missingExpectedStudyDocuments,
        missingExpectedStaffDocuments,
        missingExpectationsCount,
        expectationsUploadedFilesCount,
        isLoading,
        fileExtensionLookup,
        lastUploadedDocument,
        files,
        studyTeam,
        complion4FlagEnabled: loadHomePageV4Access(state),
    };
}, {
    deleteUnfiledDocument,
    evaluateStudyExpectations,
    unloadStudyExpectations,
    findAndAssignDuplicateDocuments,
    unloadUploadedDocuments,
    loadMimeTypes,
    removeFiles,
})(StudyExpectations);

export const ModalHelperText = () => {
    return (
        <div data-cy="StudyExpectations-ModalHelperText">
            <p>You have uploaded documents that have not yet been filed. If you leave this page, you will lose visibility to these uploaded documents on the Expectations page. Instead, We will store those docs for you in your Unfiled Docs folder under the Documents tab.</p>
            <br />
            <br />
            <p>Are you sure you want to leave this page?</p>
        </div>
    );
};

const getPath = (studyId, pathname) => {
    const subPath = pathname.includes('all-documents') ? 'all-documents' : 'missing-documents';

    return `/study/${studyId}/expectations/${subPath}`;
};


const buildTabs = (studyId, pathname, complion4FlagEnabled) => {
    const mainPath = getPath(studyId, pathname);

    return [{
        label: 'Study Documents',
        path: `${mainPath}/study`,
        backgroundHighlight: true,
    }, {
        label: 'Staff Documents',
        path: `${mainPath}/staff`,
        backgroundHighlight: true,
    }];
};

export const StudyExpectationsContent = (props) => {
    const {
        allExpectedStudyDocuments,
        allExpectedStaffDocuments,
        missingExpectedStudyDocuments,
        missingExpectedStaffDocuments,
        allowedMimeTypes,
        binderId,
        isLoading,
        studyStage,
        studyTeam,
        onError,
        onFile,
        openFilePicker,
        setUploadedFileToExpectation,
        setTeamMemberIdUpload,
        study,
        complion4FlagEnabled,
    } = props;

    if (isLoading) {
        return (
            <LoadingStatus
                actionLabel={'processing'}
                itemName={complion4FlagEnabled? 'binder expectation' : 'study expectation'}
                loading={true}
                filterQuery={false}
            />
        );
    }

    const childElement = cloneElement(Children.only(props.children),
        {
            allExpectedStudyDocuments,
            allExpectedStaffDocuments,
            missingExpectedStudyDocuments,
            missingExpectedStaffDocuments,
            allowedMimeTypes,
            binderId,
            studyStage,
            studyTeam,
            onError,
            onFile,
            openFilePicker,
            setUploadedFileToExpectation,
            setTeamMemberIdUpload,
        }); 

    const [showAddExpectationsPanel, setShowAddExpectationsPanel] = useState(false);
    const EXPECTATION_SETTINGS = `/study/${study.id}/settings/expectations/study-documents`;
    const SecuredAddExpectation = UserCanManageStudyExpectations(AddExpectationButton);
    
    const ExpectationsSettingsLink = () => 
        <Link 
            className={styles.link} 
            to={EXPECTATION_SETTINGS}
            data-cy="StudyExpectations-Link"
        >
            Expectations Settings
        </Link>;
    const SecuredExpectationsSettingsLink = UserCanManageStudyExpectations(ExpectationsSettingsLink);
    return (
        <div
            data-cy="StudyExpectations-div"
            className={
                classnames(
                    styles.expectationsContainer, { [styles.panelOpen]: showAddExpectationsPanel, }
                )
            }
        >
            <div className={styles.header}>
                <div>
                    <PageTitle>Expectations</PageTitle>
                    <PageDescription className={styles.pageDescription}>
                        This is a checklist to keep track of documents needed for the lifecycle stage this {complion4FlagEnabled ? 'binder' : 'study'} is in.
                    </PageDescription>
                </div>
                <SecuredExpectationsSettingsLink />
            </div>
            <SecuredAddExpectation onClick={
                () => setShowAddExpectationsPanel(true)
            } data-cy="StudyExpectations-onClick"/>
            <Tabs
                className={styles.tabs}
                tabs={buildTabs(study.id, location.pathname, complion4FlagEnabled)}
                location={location}
            />
            <div className={styles.expectationsBody} data-cy="StudyExpectations-childElement">
                {childElement}
            </div>
            <Conditional condition={showAddExpectationsPanel}>
                <div className={styles.addExpectationsPanel} data-cy="StudyExpectations-divs">
                    <AddExpectationsPanel
                        {...props}
                        show={showAddExpectationsPanel}
                        onDismiss={
                            () => setShowAddExpectationsPanel(false)
                        }
                    />
                </div>
            </Conditional>
        </div>
    );
};

export const AddExpectationButton = ({ onClick }) =>
    <div data-cy="StudyExpectations-btn">
        <Button
            bgcolor={BUTTON.BGCOLOR.BLUE}
            color={BUTTON.COLOR.WHITE}
            onClick={onClick}
            data-cy="StudyExpectations-onClicks"
        >
            Add Expectations
        </Button>
    </div>;

export const RecommendationMessage = ({ currentStage, message }) => {
    const studyStage = isStudyStartUp(currentStage) ?
        STUDY_START_UP :
        OTHER_STAGES;

    return (
        <div className={styles.messageContainer}>
            {message[studyStage]}
        </div>
    );
};
